import React, { useEffect, useState } from 'react';
import { ArrowLeft, X, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const WorkflowTriggerDetailPanel = ({
  trigger,
  categoryLabel,
  variant = 'dark',
  onBack,
  onClose,
  onSave,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'trigger-detail',
  chromeHeight,
}) => {
  const isDark = variant === 'dark';
  const [name, setName] = useState('');
  const [eventKey, setEventKey] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!trigger) return;
    setName(trigger.name || trigger.label || '');
    setEventKey(trigger.eventKey || trigger.id || '');
    setDescription(trigger.description || trigger.config?.description || '');
    setEnabled(trigger.config?.enabled !== false);
  }, [trigger]);

  if (!trigger) return null;

  const handleSave = () => {
    onSave?.({
      ...trigger,
      name: name.trim() || trigger.name,
      label: name.trim() || trigger.name,
      description: description.trim(),
      eventKey: eventKey.trim() || trigger.eventKey || trigger.id,
      config: {
        ...(trigger.config || {}),
        event_key: eventKey.trim() || trigger.eventKey || trigger.id,
        trigger_key: eventKey.trim() || trigger.eventKey || trigger.id,
        event: eventKey.trim() || trigger.eventKey || trigger.id,
        description: description.trim(),
        enabled,
      },
    });
  };

  return (
    <WorkflowStepEditorShell
      isOpen
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={cn(
        'border-l shadow-2xl',
        isDark ? 'border-zinc-700 bg-zinc-900' : 'border-slate-200 bg-white',
      )}
      testId="workflow-trigger-detail-panel"
    >
      <div className={cn('flex items-center justify-between border-b p-4', isDark ? 'border-zinc-700' : 'border-slate-200')}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className={cn('rounded-lg p-1.5', isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100')}
            data-testid="trigger-detail-back"
          >
            <ArrowLeft size={18} className={isDark ? 'text-zinc-400' : 'text-slate-500'} />
          </button>
          <div className="flex items-center gap-3">
            <div className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg',
              isDark ? 'bg-emerald-500/20' : 'bg-teal-100',
            )}
            >
              <Zap size={20} className={isDark ? 'text-emerald-400' : 'text-teal-600'} />
            </div>
            <div>
              <h3 className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                {name || trigger.name}
              </h3>
              <p className={cn('text-xs', isDark ? 'text-zinc-500' : 'text-slate-500')}>Trigger</p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn('rounded-lg p-2', isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100')}
        >
          <X size={18} className={isDark ? 'text-zinc-400' : 'text-slate-500'} />
        </button>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        <div className={cn(
          'rounded-lg border p-3',
          isDark ? 'border-zinc-800 bg-zinc-800/40' : 'border-slate-200 bg-slate-50',
        )}
        >
          <p className={cn('text-sm', isDark ? 'text-zinc-300' : 'text-slate-600')}>
            Configure how this workflow trigger is labeled and described in the builder.
          </p>
          {categoryLabel ? (
            <p className={cn('mt-2 text-xs', isDark ? 'text-zinc-500' : 'text-slate-500')}>
              Category: <span className={isDark ? 'text-zinc-400' : 'text-slate-700'}>{categoryLabel}</span>
            </p>
          ) : null}
          {eventKey ? (
            <p className={cn('mt-1 text-xs', isDark ? 'text-zinc-500' : 'text-slate-500')}>
              Event key: <span className={isDark ? 'text-zinc-400' : 'text-slate-700'}>{eventKey}</span>
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label className={isDark ? 'text-zinc-300' : undefined}>Trigger Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={isDark ? 'border-zinc-700 bg-zinc-800 text-white' : undefined}
          />
        </div>

        <div className="space-y-2">
          <Label className={isDark ? 'text-zinc-300' : undefined}>Event Key</Label>
          <Input
            value={eventKey}
            onChange={(e) => setEventKey(e.target.value)}
            className={isDark ? 'border-zinc-700 bg-zinc-800 text-white' : undefined}
          />
          <p className={cn('text-xs', isDark ? 'text-zinc-500' : 'text-slate-500')}>
            System event that starts or matches this trigger.
          </p>
        </div>

        <div className="space-y-2">
          <Label className={isDark ? 'text-zinc-300' : undefined}>Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={isDark ? 'border-zinc-700 bg-zinc-800 text-white' : undefined}
          />
        </div>

        <div className={cn(
          'flex items-center justify-between rounded-lg border p-3',
          isDark ? 'border-zinc-800' : 'border-slate-200',
        )}
        >
          <div>
            <p className={cn('text-sm font-medium', isDark ? 'text-white' : 'text-slate-900')}>Enabled</p>
            <p className={cn('text-xs', isDark ? 'text-zinc-500' : 'text-slate-500')}>
              Include this trigger in the workflow.
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>
      </div>

      <div className={cn('flex justify-end gap-3 border-t p-4', isDark ? 'border-zinc-700' : 'border-slate-200')}>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className={isDark ? 'border-zinc-700 text-zinc-300' : undefined}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          className={isDark ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}
          data-testid="trigger-detail-save"
        >
          Save Trigger
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default WorkflowTriggerDetailPanel;
